/* =============================================================================
   VIEWS / ADMIN / SETTINGS-GDPR — DSGVO-Kunden-Datenanonymisierung (Session 41)
   =============================================================================
   S79 REF-6: aus settings.js extrahiert. UI-Schale um `anonymize_customer_data`-
   RPC (Migration 73, S39) + JS-Wrapper (`src/core/gdpr.js`, S40). Wird im
   Compliance-Sub-Tab (DSGVO) gerendert — `renderComplianceDsgvoSubsection`
   (settings-compliance.js) ruft `renderGdprSection()` auf.

   Pflicht-Flow gemäß Art. 17/21 DSGVO:
     1. Client-Side-Validation (Email-Format, Reason-Whitelist)
     2. Rollen-Check (owner/admin/manager) — Server prüft nochmal
     3. Styled Confirm mit Challenge-Eingabe (Wort ANONYMISIEREN tippen) — S190
     4. RPC-Call → Outcome-Switch → Toast + Audit-Log-Refresh

   §147 AO bleibt unangetastet: bon_archive.html-Snapshot wird nicht
   modifiziert (siehe Anlage F.7 + Anlage G der Verfahrensdoku).

   Anti-Circular (vgl. settings-delivery-rules.js): keine Imports aus settings.js.
   Inline-Handler (triggerGdprSearch/triggerGdprAnonymize/gdprUseProfileEmail)
   resolven zur Laufzeit über window; Audit-Refresh über window.App.storage.audit
   + window.renderAuditLog. Die Test-Hooks + Action-Handler werden in settings.js
   re-exportiert (Backward-Compat für settings.test.js).
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import {
  GDPR_REASONS,
  GDPR_REASON_DEFAULT,
  anonymizeCustomerData,
  previewCustomerData,
  searchCustomerProfiles,
  _buildPreviewSummaryText,
  _classifyQueryInput
} from '../../core/gdpr.js';

/** Lookup-Map für Reason-Labels in der UI. */
const GDPR_REASON_LABELS = Object.freeze({
  gdpr_request:      'Art. 17 — Recht auf Löschung',
  gdpr_objection:    'Art. 21 — Widerspruch gegen Verarbeitung',
  admin_cleanup:     'Admin-Reinigung (z.B. Mitarbeiter-Daten)',
  duplicate_account: 'Doppelter Kunden-Account'
});

/** Pure: Label oder Pass-Through. */
export function _buildGdprReasonLabel(reason) {
  if (typeof reason !== 'string') return '';
  return GDPR_REASON_LABELS[reason] || reason;
}

/**
 * Pure: Stufe-1-Confirm-Text (native confirm-Dialog). Listet die betroffenen
 * Tabellen explizit auf, damit klar wird, was passiert.
 */
export function _buildGdprConfirmMessage(email, reason) {
  const e = typeof email === 'string' ? email.trim() : '';
  const r = _buildGdprReasonLabel(reason) || reason;
  return (
    'DSGVO-Anonymisierung für «' + e + '» starten?\n\n'
    + 'Grund: ' + r + '\n\n'
    + 'Folgendes passiert IRREVERSIBEL:\n'
    + '  • Reservierungen: Name → Pseudonym, Email/Telefon → NULL\n'
    + '  • Bestellungen: Customer-Name → Pseudonym, Email/Telefon → NULL\n'
    + '  • Bon-Archiv: Spalten anonymisiert (HTML-Snapshot bleibt — §147 AO)\n'
    + '  • Mail-Outbox: Empfänger-Mail → [anonymized]\n'
    + '  • Audit-Log: Eintrag mit sha256-Email-Hash (KEIN Klartext)\n\n'
    + 'GoBD-Felder (Beträge, TSE, Invoice-Nummer) bleiben unangetastet.\n\n'
    + 'Fortfahren?'
  );
}

/** Pure: Challenge-Prompt-Text. */
export function _buildGdprFinalChallenge() {
  return (
    'Bitte das Wort ANONYMISIEREN tippen, um die Aktion endgültig zu '
    + 'bestätigen.\n\n(Abbrechen lässt den Datensatz unverändert.)'
  );
}

/** Pure: Prüft, ob die Challenge-Eingabe akzeptiert wird (case-insensitive, trim). */
export function _validateGdprChallenge(input) {
  if (typeof input !== 'string') return false;
  return input.trim().toUpperCase() === 'ANONYMISIEREN';
}

/** Pure: Erfolgs-Toast-Text mit Counts. */
export function _buildGdprSuccessToast(data) {
  const d = data && typeof data === 'object' ? data : {};
  const counts = [
    d.reservations_updated || 0,
    'Reservierungen',
    '·',
    d.orders_updated || 0,
    'Bestellungen',
    '·',
    d.bons_updated || 0,
    'Bons',
    '·',
    d.outbox_purged || 0,
    'Mails'
  ].join(' ');
  const pseudo = d.pseudonym ? ' [' + d.pseudonym + ']' : '';
  return 'DSGVO-Anonymisierung OK: ' + counts + pseudo;
}

/* -------------------------------------------------------------------------- */
/* S42 — DSGVO-Vorschau-Cache (Module-State)                                   */
/* -------------------------------------------------------------------------- */
// Beim Klick auf "Vorschau anzeigen" wird hier die letzte Outcome-Hülle samt
// Email gehalten. Der eigentliche Anonymisieren-Aktor (triggerGdprAnonymize)
// kann so vor dem confirm-Dialog prüfen:
//   - ist die Vorschau überhaupt aktuell für die aktuell eingetippte Email?
//   - ist total=0 (No-Op) → freundlicher Abbruch statt RPC-Roundtrip
// Memory `feedback_gg_hard_way`: lieber sauber State halten als implizit aus
// dem DOM zu rekonstruieren.

let _lastGdprPreview = null; // { email, counts, total } | null

/** Pure: HTML einer Preview-Counts-Karte. Erwartet GdprPreviewOutcome.data. */
export function _buildGdprPreviewCardHtml(previewData) {
  if (!previewData || typeof previewData !== 'object') return '';
  const e = htmlEscape(previewData.email || '');
  const c = previewData.counts || {};
  const total = Number.isFinite(previewData.total) ? previewData.total : 0;

  // Wenn total=0, machen wir die Karte sofort als „No-Op"-Hinweis bunt, damit
  // der Anonymisieren-Button-Klick nicht erst durch zwei Dialoge laufen muss.
  const isNoOp = total === 0;
  const cls = isNoOp ? 'gdpr-preview-card is-noop' : 'gdpr-preview-card';
  const titleEmoji = isNoOp ? '🟡' : '🔎';
  const subTitle = isNoOp
    ? 'Keine Treffer — die Anonymisierung wäre ein No-Op.'
    : 'Diese Datensätze würden anonymisiert.';

  return `
    <div class="${cls}" id="gdpr-preview-output" data-total="${total}">
      <div class="gdpr-preview-head">
        ${titleEmoji} Vorschau für <code>${e}</code>
      </div>
      <div class="gdpr-preview-sub" style="color: var(--ink-soft); font-size: .85rem; margin-bottom: 8px;">
        ${subTitle}
      </div>
      <ul style="margin: 0; padding-left: 18px; line-height: 1.55;">
        <li><strong>${Number.isFinite(c.reservations) ? c.reservations : 0}</strong> Reservierungen</li>
        <li><strong>${Number.isFinite(c.orders) ? c.orders : 0}</strong> Bestellungen</li>
        <li><strong>${Number.isFinite(c.bons) ? c.bons : 0}</strong> Bons (HTML-Snapshot bleibt — §147 AO)</li>
        <li><strong>${Number.isFinite(c.outbox) ? c.outbox : 0}</strong> Mail-Outbox-Einträge</li>
      </ul>
      <div class="hint" style="margin-top: 10px;">
        Vorschau ist nur eine Lese-Operation. Erst der rote „Anonymisieren …"-Button
        startet den irreversiblen RPC.
      </div>
    </div>
  `;
}

/** Pure: HTML für leeren Preview-Container (Default-Stand). */
export function _buildGdprPreviewEmptyHtml() {
  return `<div id="gdpr-preview-output" class="gdpr-preview-empty hint">
    Noch keine Vorschau angefordert. Email eintragen → „Vorschau anzeigen".
  </div>`;
}

/** Pure: Test-Hook — gibt den aktuellen Preview-Cache zurück. */
export function _getLastGdprPreviewForTests() { return _lastGdprPreview; }
/** Pure: Test-Hook — setzt den Preview-Cache. */
export function _setLastGdprPreviewForTests(v) { _lastGdprPreview = v || null; }
/** Pure: Test-Hook — räumt den Preview-Cache ab. */
export function _resetLastGdprPreviewForTests() { _lastGdprPreview = null; }

/* -------------------------------------------------------------------------- */
/* S42-B.1.2 — Customer-Suche per Name / Phone                                  */
/* -------------------------------------------------------------------------- */

/** Pure: HTML einer einzelnen Customer-Profile-Karte (Trefferliste). */
export function _buildGdprProfileCardHtml(profile) {
  if (!profile || typeof profile !== 'object') return '';
  const email = htmlEscape(profile.email || '');
  const name = htmlEscape(profile.name || '—');
  const phone = htmlEscape(profile.phone || '—');
  const res = Number.isFinite(profile.reservationCount) ? profile.reservationCount : 0;
  const ord = Number.isFinite(profile.orderCount) ? profile.orderCount : 0;
  const last = profile.lastActivityAt
    ? new Date(profile.lastActivityAt).toLocaleDateString('de-DE')
    : '—';
  // Email als data-Attribut → Handler liest sie zurück und schreibt ins Input.
  return `
    <div class="gdpr-profile-card">
      <div class="gdpr-profile-head">
        <strong>${name}</strong>
        <span class="gdpr-profile-meta">Letzte Aktivität: ${last}</span>
      </div>
      <div class="gdpr-profile-row">
        <span class="gdpr-profile-label">Email</span>
        <code>${email}</code>
      </div>
      <div class="gdpr-profile-row">
        <span class="gdpr-profile-label">Telefon</span>
        <span>${phone}</span>
      </div>
      <div class="gdpr-profile-row">
        <span class="gdpr-profile-counts">
          <span>${res}</span> Reservierungen · <span>${ord}</span> Bestellungen
        </span>
        <button class="btn btn-secondary btn-xs"
                onclick="gdprUseProfileEmail('${email}')">
          → Email für Vorschau übernehmen
        </button>
      </div>
    </div>
  `;
}

/** Pure: HTML der gesamten Profile-Liste (oder Empty-Hinweis bei 0 Treffern). */
export function _buildGdprProfileListHtml(profiles, query) {
  const list = Array.isArray(profiles) ? profiles : [];
  const q = htmlEscape(query || '');
  if (list.length === 0) {
    return `<div class="gdpr-profile-empty hint">
      Keine Treffer für <code>${q}</code> in Reservierungen oder Bestellungen.
    </div>`;
  }
  const header = `<div class="gdpr-profile-header">
    <strong>${list.length}</strong> ${list.length === 1 ? 'Treffer' : 'Treffer'} für <code>${q}</code>:
  </div>`;
  return header + '<div class="gdpr-profile-list">' + list.map(_buildGdprProfileCardHtml).join('') + '</div>';
}

/** Pure: ErrorKind → menschen-lesbarer Toast-Text. */
export function _buildGdprErrorToast(outcome) {
  if (!outcome || typeof outcome !== 'object') {
    return 'DSGVO-Anonymisierung fehlgeschlagen.';
  }
  const kind = outcome.kind || 'unknown';
  switch (kind) {
    case 'invalid_email':
      return 'Email-Format ungültig — bitte prüfen.';
    case 'unauthenticated':
      return 'Bitte erneut anmelden (Session abgelaufen).';
    case 'forbidden':
      return 'Keine Berechtigung — nur owner/admin/manager dürfen anonymisieren.';
    case 'not_found':
      return 'RPC nicht verfügbar — bitte Support kontaktieren.';
    default:
      return outcome.message || 'DSGVO-Anonymisierung fehlgeschlagen.';
  }
}

/** Liefert true, wenn der eingeloggte User die DSGVO-Aktion ausführen darf. */
function _hasGdprPermission() {
  const auth = window.App && window.App.auth;
  if (!auth || typeof auth.requireRole !== 'function') return false;
  // Server-RPC akzeptiert manager+, also UI auch ab manager freischalten.
  return auth.requireRole('manager');
}

export function renderGdprSection() {
  const allowed = _hasGdprPermission();
  if (!allowed) {
    return `
      <div class="card" id="gdpr-settings-card" style="margin-top: 20px;">
        <h2>🛡️ Datenschutz / DSGVO</h2>
        <p class="hint">
          Kunden-Daten-Anonymisierung ist nur für owner / admin / manager
          freigeschaltet. Bitte mit entsprechender Rolle einloggen.
        </p>
      </div>
    `;
  }

  const reasonOptions = GDPR_REASONS.map(r => {
    const label = _buildGdprReasonLabel(r);
    const sel = r === GDPR_REASON_DEFAULT ? ' selected' : '';
    return `<option value="${htmlEscape(r)}"${sel}>${htmlEscape(label)}</option>`;
  }).join('');

  // S82-5: settings-ro-exempt — die DSGVO-Anonymisierung ist eine eigenständige
  // Manager-AKTION (Permission `dsgvo.anonymize`), kein Settings-Schreibvorgang.
  // Damit bleibt sie im read-only Compliance-Tab des Managers bedienbar
  // (siehe _disableSettingsInputs-Carve-Out in settings.js).
  return `
    <div class="card settings-ro-exempt" id="gdpr-settings-card" style="margin-top: 20px;">
      <h2>🛡️ Datenschutz / DSGVO-Anonymisierung</h2>
      <p class="hint" style="margin-bottom: 12px;">
        Pseudonymisiert die PII-Felder (Name, Email, Telefon) eines Kunden
        in allen Tabellen — Reservierungen, Bestellungen, Bon-Archiv, Mail-Outbox.
        GoBD-Felder bleiben gemäß §147 AO unangetastet, der Bon-Archiv-HTML-Snapshot
        wird NICHT überschrieben.
      </p>
      <p class="hint" style="margin-bottom: 14px; color: var(--ink-soft);">
        Aufruf protokolliert sich im <strong>Aktivitäts-Log</strong> mit
        sha256-Email-Hash (kein Klartext). Vorgang ist
        <strong>irreversibel</strong>.
      </p>

      <div class="field">
        <label for="gdpr-email-input">Suche: Email, Name oder Telefon</label>
        <input type="text"
               id="gdpr-email-input"
               placeholder="z. B. kunde@example.com  oder  Müller  oder  +49 30 12345"
               autocomplete="off">
        <div class="hint">
          Bei Eingabe einer Email läuft die Vorschau direkt. Bei Name oder
          Telefonnummer erscheint eine Trefferliste — Klick auf einen Treffer
          übernimmt die zugehörige Email für die Anonymisierung.
        </div>
      </div>

      <div class="field">
        <label for="gdpr-reason-select">Grund (Whitelist)</label>
        <select id="gdpr-reason-select">
          ${reasonOptions}
        </select>
        <div class="hint">
          Der Grund landet zusätzlich im Audit-Log und ist später für die
          Auskunfts-Antwort an den Betroffenen relevant.
        </div>
      </div>

      <div class="btn-row" style="margin-top: 12px;">
        <button class="btn btn-secondary btn-sm" id="gdpr-search-btn" onclick="triggerGdprSearch()">
          🔎 Suchen / Vorschau
        </button>
        <button class="btn btn-danger btn-sm" onclick="triggerGdprAnonymize()">
          🛡️ Anonymisieren …
        </button>
      </div>

      <div id="gdpr-search-container" style="margin-top: 14px;"></div>

      <div id="gdpr-preview-container" style="margin-top: 14px;">
        ${_buildGdprPreviewEmptyHtml()}
      </div>
    </div>
  `;
}

/**
 * S42-B.1.2 — Such-Action-Handler.
 * Wird vom "🔎 Suchen / Vorschau"-Button getriggert. Klassifiziert die
 * Eingabe (Email vs. Name vs. Phone) und ruft entweder direkt die Email-
 * Vorschau oder rendert eine Trefferliste mit Customer-Profilen.
 *
 * Wirft NIE — alle Pfade enden in Toast und/oder Search-Container.
 */
export async function triggerGdprSearch() {
  if (!_hasGdprPermission()) {
    showToast('Keine Berechtigung für DSGVO-Suche.', 'error');
    return;
  }

  const inputEl = document.getElementById('gdpr-email-input');
  const raw     = inputEl ? inputEl.value : '';
  const trimmed = (raw || '').trim();

  if (!trimmed) {
    showToast('Bitte eine Email, einen Namen oder eine Telefonnummer eingeben.', 'error');
    if (inputEl) inputEl.focus();
    return;
  }

  const kind = _classifyQueryInput(trimmed);

  // Bei einer (validen) Email → direkt den Vorschau-Pfad delegieren.
  // Das ist UX-konsistent mit dem alten Button-Verhalten.
  if (kind === 'email') {
    const searchContainer = document.getElementById('gdpr-search-container');
    if (searchContainer) searchContainer.innerHTML = '';
    await triggerGdprPreview();
    return;
  }

  if (kind === 'invalid') {
    showToast('Eingabe nicht erkannt — bitte Email, Name (≥2 Buchstaben) oder Telefon (≥3 Ziffern).', 'error');
    return;
  }

  // Sonst: Name- oder Phone-Suche.
  const btn = document.getElementById('gdpr-search-btn');
  if (btn) btn.disabled = true;

  let outcome;
  try {
    outcome = await searchCustomerProfiles(trimmed);
  } catch (err) {
    console.error('[settings.triggerGdprSearch] unerwarteter Wurf:', err);
    showToast('Suche fehlgeschlagen: ' + (err.message || err), 'error');
    if (btn) btn.disabled = false;
    return;
  }

  const container = document.getElementById('gdpr-search-container');

  if (outcome && outcome.ok) {
    const profiles = (outcome.data && outcome.data.profiles) || [];
    if (container) container.innerHTML = _buildGdprProfileListHtml(profiles, trimmed);
    const n = profiles.length;
    showToast(
      n === 0
        ? 'Keine Treffer für «' + trimmed + '».'
        : n + ' Treffer für «' + trimmed + '» — Email auswählen für die Vorschau.',
      n === 0 ? 'info' : 'success'
    );
  } else {
    if (container) container.innerHTML = '';
    showToast(_buildGdprErrorToast(outcome), 'error');
  }

  if (btn) btn.disabled = false;
}

/**
 * S42-B.1.2 — Übernimmt eine Email aus einer Profile-Karte ins Such-Feld
 * und triggert direkt die Vorschau. Memory `feedback_gg_no_view_hijack`:
 * kein Tab-Wechsel, kein Auto-Scroll außerhalb des Containers.
 */
export async function gdprUseProfileEmail(email) {
  const inputEl = document.getElementById('gdpr-email-input');
  if (inputEl && typeof email === 'string') {
    inputEl.value = email;
  }
  // Suche-Container leeren, damit der Operator klar sieht: "wir sind jetzt
  // im Vorschau-Modus für diese Email".
  const searchContainer = document.getElementById('gdpr-search-container');
  if (searchContainer) searchContainer.innerHTML = '';
  await triggerGdprPreview();
}

/**
 * S42 — DSGVO-Vorschau-Action-Handler.
 * Settings → Compliance → "🔎 Vorschau anzeigen"-Button. Liest die vier
 * betroffenen Tabellen frisch aus DB (Memory `feedback_gg_export_fresh_db`)
 * und rendert eine Counts-Karte ins #gdpr-preview-container-Div. Speichert
 * das Resultat zusätzlich in `_lastGdprPreview` für die spätere
 * triggerGdprAnonymize-Vorprüfung.
 *
 * Wirft NIE — alle Pfade enden in Toast + Output-Container.
 */
export async function triggerGdprPreview() {
  if (!_hasGdprPermission()) {
    showToast('Keine Berechtigung für DSGVO-Vorschau.', 'error');
    return;
  }

  const emailEl = document.getElementById('gdpr-email-input');
  const email   = emailEl ? emailEl.value : '';

  if (!email || !email.trim()) {
    showToast('Bitte eine Customer-Email eintragen.', 'error');
    if (emailEl) emailEl.focus();
    return;
  }

  const btn = document.getElementById('gdpr-preview-btn');
  if (btn) btn.disabled = true;

  let outcome;
  try {
    outcome = await previewCustomerData(email);
  } catch (err) {
    console.error('[settings.triggerGdprPreview] unerwarteter Wurf:', err);
    showToast('Vorschau-Abfrage fehlgeschlagen: ' + (err.message || err), 'error');
    if (btn) btn.disabled = false;
    return;
  }

  const container = document.getElementById('gdpr-preview-container');

  if (outcome && outcome.ok) {
    _lastGdprPreview = outcome.data;
    if (container) container.innerHTML = _buildGdprPreviewCardHtml(outcome.data);
    const totalInfo = (outcome.data && Number.isFinite(outcome.data.total))
      ? outcome.data.total : 0;
    showToast(_buildPreviewSummaryText(outcome.data.email, outcome.data.counts),
              totalInfo === 0 ? 'info' : 'success');
  } else {
    _lastGdprPreview = null;
    if (container) container.innerHTML = _buildGdprPreviewEmptyHtml();
    showToast(_buildGdprErrorToast(outcome), 'error');
  }

  if (btn) btn.disabled = false;
}

/**
 * DSGVO-Action-Handler — Settings → Compliance → "Anonymisieren …"-Button.
 * Zweistufiger Confirm (native confirm + Challenge-Prompt), Outcome-Switch,
 * Audit-Log-Refresh. Wirft nie — alle Pfade enden in einem Toast.
 *
 * Verträge:
 *  - Pre: `App.auth.requireRole('manager')` muss true sein. UI versteckt den
 *         Button sonst, aber wir prüfen defensiv nochmal.
 *  - Post: Bei `ok=true` Audit-Log-Tab refreshen, damit der neue Eintrag
 *          sofort sichtbar ist (Memory `feedback_gg_no_view_hijack` —
 *          KEIN automatischer Tab-Wechsel).
 *
 *  S42-Erweiterung: Wenn `_lastGdprPreview` zur eingetippten Email passt
 *  UND total=0, brechen wir freundlich ab (No-Op-Schutz). Wenn die letzte
 *  Vorschau zu EINER ANDEREN Email war, weisen wir zusätzlich darauf hin —
 *  hilft Versehen mit Tippfehlern zu vermeiden, ohne den Flow zu blocken.
 */
export async function triggerGdprAnonymize() {
  if (!_hasGdprPermission()) {
    showToast('Keine Berechtigung für DSGVO-Anonymisierung.', 'error');
    return;
  }

  const emailEl  = document.getElementById('gdpr-email-input');
  const reasonEl = document.getElementById('gdpr-reason-select');
  const email    = emailEl ? emailEl.value : '';
  const reason   = reasonEl ? reasonEl.value : GDPR_REASON_DEFAULT;

  if (!email || !email.trim()) {
    showToast('Bitte eine Customer-Email eintragen.', 'error');
    if (emailEl) emailEl.focus();
    return;
  }

  // S42 — Preview-Aware Vorprüfung. Wenn `_lastGdprPreview` zur aktuellen
  // Email passt UND keine Treffer hat, brechen wir hier ab. Verhindert,
  // dass der Operator zwei Dialog-Modale durchwinkt und dann staunt, dass
  // 0 Datensätze betroffen waren.
  const trimmedEmail = email.trim();
  if (_lastGdprPreview
      && typeof _lastGdprPreview.email === 'string'
      && _lastGdprPreview.email === trimmedEmail
      && _lastGdprPreview.total === 0) {
    showToast(
      'Keine Datensätze für «' + trimmedEmail + '» gefunden. Anonymisierung übersprungen.',
      'info'
    );
    return;
  }

  // S190 — Stufe 1+2 in EINEM styled Confirm: komplette Beschreibung + Challenge-
  // Eingabe (das Wort ANONYMISIEREN tippen aktiviert erst „Anonymisieren"). Ersetzt
  // window.confirm + window.prompt. _validateGdprChallenge/_buildGdprFinalChallenge
  // bleiben exportiert (Tests). Der Challenge-Gate (challengeMatches) ist äquivalent.
  const _gate = await window.styledConfirm({
    title: 'Kundendaten anonymisieren?', danger: true, confirmLabel: 'Anonymisieren',
    body: _buildGdprConfirmMessage(email, reason),
    challenge: 'ANONYMISIEREN'
  });
  if (!_gate.confirmed) return;

  // Button disablen während der RPC läuft, damit Doppel-Klick nicht doppelt feuert.
  const card = document.getElementById('gdpr-settings-card');
  if (card) card.querySelectorAll('button').forEach(b => b.disabled = true);

  let outcome;
  try {
    outcome = await anonymizeCustomerData(email, reason);
  } catch (err) {
    // anonymizeCustomerData wirft eigentlich nicht — defensiv abfangen.
    console.error('[settings.triggerGdprAnonymize] unerwarteter Wurf:', err);
    showToast('Unerwarteter Fehler: ' + (err.message || err), 'error');
    if (card) card.querySelectorAll('button').forEach(b => b.disabled = false);
    return;
  }

  if (outcome && outcome.ok) {
    showToast(_buildGdprSuccessToast(outcome.data), 'success');
    if (emailEl) emailEl.value = '';
    if (reasonEl) reasonEl.value = GDPR_REASON_DEFAULT;

    // S42 — Preview-Cache räumen + UI-Container zurücksetzen, sonst hängt
    // eine veraltete Vorschau-Karte mit Counts, die jetzt 0 sind.
    _lastGdprPreview = null;
    const previewContainer = document.getElementById('gdpr-preview-container');
    if (previewContainer) previewContainer.innerHTML = _buildGdprPreviewEmptyHtml();

    // Audit-Cache aus DB neu laden: der RPC hat server-seitig in `audit_log`
    // geschrieben (`action='customer.anonymized'`), der Client-Cache
    // `_state.auditLog` ist out-of-sync. Ohne Reload würde der neue Eintrag
    // weder in der Liste noch im DSGVO-Filter-Count auftauchen. (S41-Bugfix.)
    const auditStore = window.App && window.App.storage && window.App.storage.audit;
    if (auditStore && typeof auditStore.reload === 'function') {
      try { await auditStore.reload(); } catch (e) {
        console.warn('[settings.triggerGdprAnonymize] audit.reload:', e);
      }
    }

    // S180 — Audit-Log-Tab entfernt; kein In-App-Refresh mehr nötig (Audit-Trail
    // bleibt DB-seitig in audit_log, on-demand/Export).
  } else {
    showToast(_buildGdprErrorToast(outcome), 'error');
  }

  if (card) card.querySelectorAll('button').forEach(b => b.disabled = false);
}
